<template>
	<TnCardHorizontal
		v-if="loading || (preConfigBundle && (hasInventory || isTest))"
		dashed
		border
		shadow="s"
		:class="{ 'padding-top-2xl': preConfigBundle?.description && bundleProduct, 'default-cursor': true }"
		ref="pre-config-bundle"
	>
		<template #badge>
			<TnBadge
				v-if="preConfigBundle?.description && bundleProduct && !loading"
				responsive
			>
				{{ preConfigBundle.description }}
			</TnBadge>
		</template>
		<template #graphic>
			<TnSkeleton
				width="50"
				height="70"
				borderRadius="s"
				v-if="loading"
			/>
			<NuxtImg
				:alt="bundleProduct.title"
				v-if="bundleProduct"
				:src="bundleProduct.imageLink"
				height="48px"
			/>
		</template>
		<template #default>
			<div v-if="loading">
				<TnSkeleton
					class="skeleton"
					width="50%"
					height="16"
					borderRadius="m"
				/>
				<TnSkeleton
					class="skeleton"
					width="60%"
					height="16"
					borderRadius="m"
				/>
				<TnSkeleton
					class="skeleton"
					width="25%"
					height="16"
					borderRadius="m"
				/>
			</div>
			<div v-else-if="!loading && bundleProduct">
				<TnParagraph
					bold
					responsive
					size="s"
					>{{ bundleType }}</TnParagraph
				>
				<TnParagraph
					responsive
					size="s"
					>{{ bundleProduct.title }}</TnParagraph
				>
				<TnParagraph
					bold
					responsive
					size="m"
					class="product-price"
					>{{ formattedSalePrice ? formattedSalePrice : formattedFullPrice }}</TnParagraph
				>
			</div>
		</template>
		<template #actions>
			<TnSkeleton
				v-if="loading"
				width="80"
				height="30"
				borderRadius="m"
			/>
			<TnButton
				v-if="!loading"
				size="xs"
				@click="addToCart(bundleProduct)"
				@mouseenter="handleMouseEnter"
				@mouseleave="handleMouseLeave"
				:iconLeft="inCart ? (mouseOver ? 'close' : 'success') : ''"
				>{{ inCart ? (mouseOver ? "Fjern" : "Lagt til") : "Legg til" }}</TnButton
			>
			<TnNotification
				v-if="error"
				type="error"
				timeout="2500"
				closeable
				position="bottom-right"
				title="Noe gikk galt"
			/>
		</template>
	</TnCardHorizontal>
</template>
<script lang="ts" setup>
import { useStore } from "vuex";
import ecommerceIntegration from "~/integrations/ecommerce-v2-integration";
import ShoppingCartItemModel from "~/helpers/ecommerce/ShoppingCartItemModel/ShoppingCartItemModel";
import { formatPrice } from "~/helpers/formatting/price";
import safeEcommerceUrl from "~/helpers/ecommerce/safeEcommerceUrl";
import { ProductCategory } from "~/components/ecommerce/types/ProductCategory";

const props = defineProps<{
	bundleType: string;
	readyForProduction: boolean;
}>();

const store = useStore();

const { eCommerceProduct } = useECommerceProduct();
const isTest = useIsTest();

const loading = ref(true);
const bundleProduct = ref(null);
const mouseOver = ref(false);
const error = ref(false);
const bundle = ref(null);

const handleMouseEnter = () => {
	mouseOver.value = true;
};

const handleMouseLeave = () => {
	mouseOver.value = false;
};

const visibleCartItems = computed(() => store.getters["shoppingCart/visibleCartItems"]);
const currentTerminal = computed(() => store.getters["checkout/currentTerminal"]);
const onlyAccessoryProducts = computed(() => store.getters["checkout/onlyAccessoryProducts"]);

const preConfigBundle = computed(() => {
	return (
		eCommerceProduct.value?.preConfigBundles?.find((x) => x.name === props.bundleType) ||
		bundle.value?.find((x) => x.name === props.bundleType)
	);
});

const prices = computed(() => {
	const fullPrice = isBusiness.value ? bundleProduct.value?.price : bundleProduct.value?.priceWithVat;
	const salePrice = isBusiness.value ? bundleProduct.value?.salePrice : bundleProduct.value?.salePriceWithVat;
	return { fullPrice, salePrice };
});

const formattedFullPrice = computed(() => {
	return prices.value.fullPrice && !isNaN(prices.value?.fullPrice)
		? formatPrice.oneTime(prices.value.fullPrice, { prefix: false, round: true })
		: undefined;
});

const formattedSalePrice = computed(() => {
	return prices.value.salePrice && !isNaN(prices.value?.salePrice)
		? formatPrice.oneTime(prices.value.salePrice, { prefix: false, round: true })
		: undefined;
});

const isBusiness = computed(() => {
	return store.getters["ecommerce/segment"] === "business";
});

const hasInventory = computed(() => {
	return bundleProduct.value?.inventory?.[0]?.amount > 0;
});

const inCart = computed(() => {
	for (const item of visibleCartItems.value) {
		if (item === undefined || item.type === "old-swap-device") return false;
		if (item.data?.id == bundleProduct.value?.id) {
			return item.id;
		}
		if (item.subItems) {
			for (const subItem of item.subItems) {
				if (subItem.data?.id === bundleProduct.value?.id) {
					return subItem.id;
				}
			}
		}
	}
	return false;
});

const loadBundle = async () => {
	if (preConfigBundle.value?.linkedProducts?.length <= 0 && !eCommerceProduct.value?.variantParentId)
		return (loading.value = false);
	if (preConfigBundle.value === undefined && eCommerceProduct.value?.variantParentId) {
		try {
			loading.value = true;
			bundle.value = await ecommerceIntegration.getPreConfigBundles(eCommerceProduct.value?.variantParentId);
			if (preConfigBundle.value?.linkedProduct) {
				return (bundleProduct.value = preConfigBundle.value?.linkedProduct);
			}
		} catch (error) {
			console.error(error.response?.data || error);
		} finally {
			loading.value = false;
		}
	}
	try {
		if (!preConfigBundle.value?.linkedProducts) return;
		loading.value = true;
		bundleProduct.value = await ecommerceIntegration.getProductById(preConfigBundle.value?.linkedProducts?.[0]);
	} catch (error) {
		console.error(error.response?.data || error);
	} finally {
		loading.value = false;
	}
};

const addToCart = async (product) => {
	error.value = false;
	if (inCart.value) {
		return await remove();
	}
	try {
		const metadata = {
			name: product?.title,
			prices: product?.prices,
			price: product?.priceWithVat,
			priceWithoutVat: product?.price,
			salePrice: product?.salePriceWithVat || undefined,
			salePriceWithoutVat: product?.salePrice || undefined,
			image: product?.imageLink,
			category: ProductCategory.ACCESSORY,
			preConfigId: preConfigBundle.value?.serviceProducts?.at(0),
			badge: preConfigBundle.value?.description,
			link: {
				to: safeEcommerceUrl(product?.category, product?.brand, product?.title, product?.condition),
			},
		};
		const id = onlyAccessoryProducts.value ? undefined : currentTerminal.value?.id;
		await store.dispatch(
			"shoppingCart/add",
			ShoppingCartItemModel("hardware", { id: product?.id, price: product?.priceWithVat, quantity: 1 }, metadata, id),
		);
	} catch (err) {
		error.value = err;
		console.log(err);
	}
};

const remove = async () => {
	try {
		await store.dispatch("shoppingCart/remove", inCart.value);
	} catch (err) {
		console.error(err);
		error.value = err;
	}
};

onMounted(() => {
	loadBundle();
});

watch(
	() => eCommerceProduct.value,
	(value) => {
		if (import.meta.client) {
			if (value?.id && !loading.value) {
				loadBundle();
			}
		}
	},
	{ immediate: true },
);
</script>
<style lang="scss" scoped>
.default-cursor {
	cursor: default;
}

.skeleton {
	margin: $spacing-s;
}

.product-price {
	color: $color-primary-mid;
	margin-top: $spacing-xs;
}
</style>
